import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { CookieModal } from '@naf-simba/sections-cookie-modal';
import { cookieDeclarationPath, customizeCookiesPath } from '@universal/apps/Themed/ThemedApp.utils';
import { trackCookieBannerClick } from '../../utils/Tracking/TrackCookies';

type CookiebotAPI = {
  hide: () => void;
  hasResponse: boolean;
  submitCustomConsent: (preferences: boolean, statistics: boolean, marketing: boolean) => void;
  dialog: { submitConsent: () => void };
};

export type CookiebotInstance = Window & { Cookiebot: CookiebotAPI };

const isCookiePage = (pathname: string) => pathname === customizeCookiesPath || pathname === cookieDeclarationPath;

export const CustomCookieModal = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const cookiebotRef = useRef<CookiebotAPI | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleCookiebotInit = useCallback(() => {
    if (window && (window as unknown as CookiebotInstance).Cookiebot) {
      const { Cookiebot } = window as unknown as CookiebotInstance;
      // Never open cookie banner when user is managing cookie consents
      if (!isCookiePage(window.location.pathname)) {
        if (!Cookiebot.hasResponse) {
          cookiebotRef.current = Cookiebot;
          setIsOpen(true);
        }
      } else {
        Cookiebot.hide();
        setIsOpen(false);
      }
    } else {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    setIsMounted(true);
    window.addEventListener('CookiebotOnDialogInit', handleCookiebotInit);

    return () => window.removeEventListener('CookiebotOnDialogInit', handleCookiebotInit);
  }, [handleCookiebotInit]);

  // After consent is withdrawn (e.g. via the /cookies declaration widget), Cookiebot won't
  // re-fire CookiebotOnDialogInit in an SPA because there's no page reload. Re-check on
  // every navigation so the banner appears immediately when hasResponse becomes false.
  useEffect(() => {
    if (isCookiePage(location.pathname)) {
      setIsOpen(false);
      return;
    }
    const cookiebot = (window as unknown as CookiebotInstance).Cookiebot;
    if (cookiebot && !cookiebot.hasResponse) {
      cookiebotRef.current = cookiebot;
      setIsOpen(true);
    }
  }, [location.pathname]);

  if (!isMounted) return null;

  return (
    <div data-elastic-exclude data-nosnippet>
      <CookieModal
        isOpen={isOpen}
        content={
          <>
            NAF benytter informasjonskapsler (cookies) til trafikkmåling og for å kunne levere en bedre
            brukeropplevelse. I noen tilfeller deler vi denne informasjonen med samarbeidspartnerne våre, slik at du får
            mest mulig relevante tilbud og produkter.
            <br />
            Du kan når som helst{' '}
            <Link to={cookieDeclarationPath}>endre eller trekke samtykke til informasjonskapsler her.</Link>
          </>
        }
        buttons={[
          {
            label: 'Tillat alle',
            onClick: () => {
              cookiebotRef.current?.dialog?.submitConsent();
              setIsOpen(false);
              trackCookieBannerClick('Tillat alle', 'preferences:true|statistics:true|marketing:true');
            },
          },
          {
            label: 'Avvis alle',
            onClick: () => {
              cookiebotRef.current?.submitCustomConsent(false, false, false);
              setIsOpen(false);
              trackCookieBannerClick('Avvis alle', 'preferences:false|statistics:false|marketing:false');
            },
          },
          {
            label: 'Tilpass innstillinger',
            onClick: () => {
              setIsOpen(false);
              navigate(customizeCookiesPath);
              trackCookieBannerClick('Tilpass innstillinger');
            },
          },
        ]}
      />
    </div>
  );
};
